import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { ArrowLeft, Check, KeyRound, LoaderCircle, Mail } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TruckMark } from "@/components/app-shell";
import { api, jsonBody } from "@/lib/api";
import { t } from "@/lib/i18n";
import { PasswordInput } from "@/components/ui/password-input";

/**
 * `POST /api/auth/forgot-password` and `POST /api/auth/reset-password/:token`
 * have existed on the server since the first release, but no client route ever
 * reached them: the "Forgot password?" link on the sign-in card pointed at a
 * path the router did not know, so the unauthenticated catch-all bounced the
 * user straight back to the form with no explanation.
 */
function AuthShell({ children }: { children: React.ReactNode }) {
    useEffect(() => {
        document.body.classList.add("auth-body");
        return () => document.body.classList.remove("auth-body");
    }, []);
    return <main className="auth-layout auth-layout-narrow">
    <section className="auth-panel">
      <div className="auth-card">
        <a className="brand auth-brand" href="/" aria-label={t("spa_driverflow_home")}><TruckMark /><span><strong>DriverFlow</strong><small>{t("brand_subtitle")}</small></span></a>
        {children}
        <Link className="text-link center" href="/login"><ArrowLeft className="ui-icon"/>{t("back_to_sign_in")}</Link>
      </div>
    </section>
  </main>;
}

const emailSchema = z.object({ email: z.string().trim().min(1, "validation_email_required").email("validation_email") });

export function ForgotPasswordPage() {
    const form = useForm<z.infer<typeof emailSchema>>({ resolver: zodResolver(emailSchema), defaultValues: { email: "" } });
    // The endpoint answers `{ ok: true }` whether or not the address exists, so
    // the page must not imply anything about which accounts are real. In
    // development it also hands back the token, which saves wiring up mail.
    const request = useMutation({
        mutationFn: (value: z.infer<typeof emailSchema>) => api<{ ok: boolean; resetToken?: string }>("/api/auth/forgot-password", { method: "POST", ...jsonBody(value) }),
    });

    if (request.isSuccess) return <AuthShell>
      <div className="auth-status"><span className="auth-status-mark"><Mail className="ui-icon"/></span>
        <h1>{t("forgot_sent_heading")}</h1>
        <p>{t("forgot_sent_body", { email: form.getValues("email") })}</p>
        {request.data.resetToken ? <a className="secondary-button full" href={`/reset-password/${request.data.resetToken}`}>{t("forgot_dev_token")}</a> : null}
        {/* No resend used to exist — a slow inbox meant re-typing the address. */}
        <button className="text-link center" type="button" disabled={request.isPending} onClick={() => request.mutate({ email: form.getValues("email") })}>{t("forgot_sent_resend")}</button>
      </div>
    </AuthShell>;

    return <AuthShell>
      <div className="auth-copy"><h1>{t("forgot_heading")}</h1><p>{t("forgot_description")}</p></div>
      {request.error ? <div className="alert danger" role="alert">{request.error.message}</div> : null}
      <form className="stack-form" onSubmit={form.handleSubmit((value) => request.mutate(value))} noValidate>
        <label>{t("login_email_label")}<input type="email" autoComplete="username" autoFocus placeholder="you@company.com" aria-invalid={Boolean(form.formState.errors.email)} {...form.register("email")}/>
          {form.formState.errors.email ? <span className="field-error">{t(form.formState.errors.email.message ?? "")}</span> : null}
        </label>
        <button className="primary-button large" type="submit" disabled={request.isPending}>{request.isPending ? <LoaderCircle className="animate-spin ui-icon"/> : <Mail className="ui-icon"/>}{t("forgot_submit")}</button>
      </form>
    </AuthShell>;
}

const passwordSchema = z.object({
    password: z.string().min(8, "error_password_min_length"),
    confirmPassword: z.string().min(1, "profile_confirm_password"),
}).refine((value) => value.password === value.confirmPassword, { path: ["confirmPassword"], message: "profile_passwords_must_match" });

export function ResetPasswordPage({ token }: { token: string }) {
    const [done, setDone] = useState(false);
    const form = useForm<z.infer<typeof passwordSchema>>({ resolver: zodResolver(passwordSchema), defaultValues: { password: "", confirmPassword: "" } });
    const save = useMutation({
        mutationFn: (value: z.infer<typeof passwordSchema>) => api(`/api/auth/reset-password/${encodeURIComponent(token)}`, { method: "POST", ...jsonBody({ password: value.password }) }),
        onSuccess: () => setDone(true),
    });
    // An invalid or expired link used to be discovered only after filling the
    // whole form, with no way forward but a small back-link. The server answers
    // 400 with a stable message — detect it once and switch to a dead-end state
    // with a direct path to request a fresh link.
    const tokenInvalid = save.error instanceof Error && /invalid|expired|token/i.test(save.error.message);

    if (done) return <AuthShell>
      <div className="auth-status"><span className="auth-status-mark success"><Check className="ui-icon"/></span>
        <h1>{t("reset_done_heading")}</h1>
        <p>{t("reset_done_body")}</p>
        <Link className="primary-button large full" href="/login">{t("btn_sign_in")}</Link>
      </div>
    </AuthShell>;

    if (tokenInvalid) return <AuthShell>
      <div className="auth-status"><span className="auth-status-mark"><KeyRound className="ui-icon"/></span>
        <h1>{t("reset_invalid_heading")}</h1>
        <p>{t("reset_invalid_body")}</p>
        <Link className="secondary-button full" href="/forgot-password">{t("reset_request_new")}</Link>
        <Link className="primary-button large full" href="/login">{t("btn_sign_in")}</Link>
      </div>
    </AuthShell>;

    return <AuthShell>
      <div className="auth-copy"><h1>{t("reset_heading")}</h1><p>{t("reset_description")}</p></div>
      {save.error ? <div className="alert danger" role="alert">{save.error.message}</div> : null}
      <form className="stack-form" onSubmit={form.handleSubmit((value) => save.mutate(value))} noValidate>
        <label>{t("profile_new_password")}
          <PasswordInput autoComplete="new-password" autoFocus aria-invalid={Boolean(form.formState.errors.password)} {...form.register("password")}/>
          <small className="hint" style={{ marginBottom: 0 }}>{t("error_password_min_length_hint")}</small>
          {form.formState.errors.password ? <span className="field-error">{t(form.formState.errors.password.message ?? "")}</span> : null}
        </label>
        <label>{t("profile_confirm_password")}<PasswordInput autoComplete="new-password" aria-invalid={Boolean(form.formState.errors.confirmPassword)} {...form.register("confirmPassword")}/>
          {form.formState.errors.confirmPassword ? <span className="field-error">{t(form.formState.errors.confirmPassword.message ?? "")}</span> : null}
        </label>
        <button className="primary-button large" type="submit" disabled={save.isPending}>{save.isPending ? <LoaderCircle className="animate-spin ui-icon"/> : <KeyRound className="ui-icon"/>}{t("reset_submit")}</button>
      </form>
    </AuthShell>;
}
